import {
  EXTERNAL_ID_SESSION_STORAGE_KEY,
  NewRelicCustomAttribute,
  isWebView,
  parseChannelFromExternalId,
} from '@cof/refi-web-sdk/dist/utils/stratum.js';
import {
  displayProgressBarEvent,
  updateMacroStepEvent,
  updateMicroStepEvent,
} from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { LitElement, html } from 'lit';
import { stratum } from '../constants/stratum.js';
import { environment } from '../environment/environment.js';
import { AppService } from './app.service.js';
import { style } from './app.style.js';
import { ApiService } from './core/services/api/api.service.js';
import { CookieService } from './core/services/cookie/cookie.service.js';
import { SessionService } from './core/services/session/session.service.js';
import { softpullAnimation } from './review/prequal-review/animations/softpullAnimation.js';
import { Router } from './router.js';
import {
  REFI_CREDITPULL_CONST,
  REFI_PAGES,
  REFI_PREQUAL_CONST,
  REFI_STATES,
} from './shared/constants/app.constants.js';
import { DebugService } from './shared/services/debug.service.js';
import { ExperimentService } from './shared/services/experiment.service.js';
import { FeatureToggleService } from './shared/services/feature-toggle.service.js';
import { PrequalAppService } from './shared/services/prequal-app.service.js';

import('./review/review.js');
import('./post-processing/post-processing.js');
import('./cxt-flow/cxt-flow.js');
import('./delayed-hardpull/delayed-hardpull.js');
import('./processing/processing.js');
import('./cxt-flow/cxt-comb-hp-event/cxt-comb-hp-event.component.js');
import('./identity-verification/identity-verification.js');

export class RefiCreditpullApp extends LitElement {
  constructor(debugServiceName = 'RefiCreditpullApp') {
    super();
    SessionService.refreshSession();
    this.setupSession();
    this.debug = new DebugService(debugServiceName);
    this.loadingApp = false;
    this.loadingAppIconic = true;
    this.onProcessingPage = false;
    if (!this.experimentParam || environment.isProdEnv) {
      this.experiments = [];
    }
    this.existingExperimentIds = [];
    this.isIconicInvisiblePrequal = false;
    this.isIconicV2 = false;
    this.isOtpExperiment = false;
    this.initiateAnimationCompletion = false;
    this.showCompletionAnimation = false;
    this.showAnimation = false;
    this.animationText = 'Checking your eligibility...';
    this.animationCompleteText = "Congrats, you're eligible!";
    this.updateStateBinded = this.updateState.bind(this);
    this.currentStep = '';
  }

  static get properties() {
    return {
      hasStips: { type: Boolean },
      loadingApp: { type: Boolean, reflect: true },
      loadingAppIconic: { type: Boolean, reflect: true },
      onProcessingPage: { type: Boolean },
      canHelp: { type: Boolean },
      status: { type: String },
      useNewProgressBar: { type: Boolean },
      showCemp: { type: Boolean, reflect: true },
      creditpullType: { type: String, attribute: 'creditpull-type' },
      postSoftpullState: { type: String, attribute: 'post-softpull-state' },
      marketingSource: { type: String, attribute: 'marketing-source' },
      application: { type: Object },
      clientCorrelationId: { type: String, attribute: 'client-correlation-id' },
      showAnimation: { type: Boolean },
      initiateAnimationCompletion: { type: Boolean },
      showCompletionAnimation: { type: Boolean },
      currentState: {
        type: Boolean,
        reflect: true,
      },
      currentStep: { type: String, attribute: 'current-step', reflect: true },
      v2Routing: {
        type: Boolean,
        attribute: 'v2-routing',
        reflect: true,
        hasChanged: (newVal) => {
          AppService.isV2Routing = newVal;
        },
      },
      experiments: {
        type: Object,
        hasChanged: (newVal, oldVal) => {
          if (newVal !== oldVal) {
            // loop through this.experiments and add the object name to experimentIds array if it's eligible
            const experimentIds = [];
            Object.keys(newVal).forEach((experiment) => {
              if (newVal[experiment].isEligible) {
                experimentIds.push(newVal[experiment].allocation);
              } else {
                experimentIds.push(`${experiment}-ineligible`);
              }
            });
            ExperimentService.updateExperiments(
              experimentIds,
              this.existingExperimentIds
            );
            this.existingExperimentIds = experimentIds;
            FeatureToggleService.updateIsIconicAndToggledOn(false);
            FeatureToggleService.updateIsCombinedHPOrDelayedHP();

            return true;
          }
          return false;
        },
      },
      featureToggles: { type: Object, attribute: 'feature-toggles' },
    };
  }

  static styles = [style];

  async connectedCallback() {
    super.connectedCallback();
    FeatureToggleService.setFeatureToggles(this.featureToggles);
    await FeatureToggleService.updateIsIconicAndToggledOn();
    await FeatureToggleService.updateIsCombinedHPOrDelayedHP();
    this.isIconicInvisiblePrequal =
      ExperimentService.isIconicInvisiblePrequalAndToggledOn;
    this.isIconicV2 = ExperimentService.isIconicV2ToggledOn;
    this.isOtpExperiment = ExperimentService.isOtp;
    this.loadingAppIconic = false;

    this.addEventListener(
      REFI_CREDITPULL_CONST.ROUTE_CHANGE,
      this.updateStateBinded
    );
    this.debug.log('setting experiments', this.experiments);
    stratum.setPluginVar(
      'refiPluginNewRelic',
      NewRelicCustomAttribute.PRODUCT_TYPE,
      SessionService.getCachedOffer()?.offerTypeCode ?? 'BAU'
    );
    stratum.setPluginVar(
      'refiPluginNewRelic',
      NewRelicCustomAttribute.IS_WEBVIEW,
      isWebView(navigator.userAgent) // null if false
    );
    stratum.setPluginVar(
      'refiPluginNewRelic',
      NewRelicCustomAttribute.EXTERNAL_ID,
      sessionStorage.getItem(EXTERNAL_ID_SESSION_STORAGE_KEY)
    );
    stratum.setPluginVar(
      'refiPluginNewRelic',
      NewRelicCustomAttribute.CHANNEL,
      parseChannelFromExternalId(
        sessionStorage.getItem(EXTERNAL_ID_SESSION_STORAGE_KEY)
      )
    );
    const parsedCookie = CookieService.getCookie(
      REFI_CREDITPULL_CONST.MARKETING_ATTRIBUTE_COOKIE
    )?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1];
    if (externalIdFromCookie && parsedCookie?.[0] === 'external_id') {
      stratum.setExternalVar('marketingChannel', externalIdFromCookie);
    }
    if (
      this.creditpullType === REFI_CREDITPULL_CONST.CREDITPULL_TYPE.SOFTPULL
    ) {
      // setup softpull
      ExperimentService.abTestsName = 'softpull-ab-tests';
      ExperimentService.updateExperiments([]);
      if (AppService.currentState === REFI_PREQUAL_CONST.STATES.REVIEW) {
        this.refreshSoftpullState();
      }
      displayProgressBarEvent(true);
      updateMicroStepEvent(
        REFI_PREQUAL_CONST.PROGRESS_BAR_MICRO_STEPS.microStepsList[
          REFI_PREQUAL_CONST.PROGRESS_BAR_MICRO_STEPS.microStepsList.length - 1
        ]
      );
      ApiService.setCorrelationId(this.clientCorrelationId);
    } else {
      // setup hardpull
      ExperimentService.setupHardpullAbTests();
      SessionService.refreshSession();
      AppService.refreshSession();
      this.setupSession();
      if (
        ExperimentService.isCombinedHardpullContractToggledOn ||
        ExperimentService.isDelayedHardpullToggledOn ||
        this.currentState === 'credit-consent'
      ) {
        displayProgressBarEvent(false);
      } else {
        displayProgressBarEvent(true);
        updateMacroStepEvent('Lock in payment', [
          'Offers',
          'Review',
          'Contract',
        ]);
        updateMicroStepEvent('Offers');
      }
      AppService.creditpullType =
        REFI_CREDITPULL_CONST.CREDITPULL_TYPE.HARDPULL;
      AppService.updateState(AppService.state);
      this.currentState = AppService.currentState;
      ApiService.setCorrelationId('');
    }
  }

  async disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(
      REFI_CREDITPULL_CONST.ROUTE_CHANGE,
      this.updateStateBinded
    );
  }

  renderSpinner() {
    return html`<refi-loading-spinner class="_gb-show"></refi-loading-spinner>`;
  }

  renderPage(currentState) {
    // Render based on current state
    switch (currentState) {
      case REFI_STATES.CXT_COMBINED_HARDPULL:
        return html`<refi-cxt-combined-hardpull></refi-cxt-combined-hardpull>`;
      case REFI_STATES.CREDIT_CONSENT:
        return html`<hardpull-primer></hardpull-primer>`;
      case REFI_STATES.DELAYED_HARDPULL_PRIMER:
        return html`<delayed-hardpull-primer></delayed-hardpull-primer>`;
      case REFI_STATES.DELAYED_HARDPULL_CONSENT:
        return html`<delayed-hardpull-consent></delayed-hardpull-consent>`;
      case REFI_STATES.COMBINED_HP_CONTRACT:
        return html`<cxt-combined-hardpull></cxt-combined-hardpull>`;
      case REFI_STATES.HARDPULL_REVIEWING:
        return html`<refi-reviewing-hardpull
          class="side-padding"
        ></refi-reviewing-hardpull>`;
      case REFI_STATES.HARDPULL_PROCESSING:
        return html`<refi-processing-hardpull
          class="side-padding"
        ></refi-processing-hardpull>`;
      case REFI_STATES.HARDPULL_DECLINED:
        return html`<refi-hardpull-decline
          class="side-padding"
        ></refi-hardpull-decline>`;
      case REFI_PREQUAL_CONST.STATES.REVIEW:
        return html`<refi-prequalify-review
          class="side-padding"
          @internal-show-animation="${() => {
            this.showAnimation = true;
          }}"
          @initiate-animation-completion="${() => {
            this.initiateAnimationCompletion = true;
          }}"
        ></refi-prequalify-review>`;
      case REFI_PREQUAL_CONST.STATES.REVIEWING:
        if (this.isIconicInvisiblePrequal) {
          return html`<iconic-under-review
            class="side-padding"
          ></iconic-under-review>`;
        }
        return html`<refi-prequal-under-review
          class="side-padding"
        ></refi-prequal-under-review>`;
      case REFI_PREQUAL_CONST.STATES.NO_OFFER:
        if (this.isIconicInvisiblePrequal) {
          return html`<iconic-no-offer class="side-padding"></iconic-no-offer>`;
        }
        return html`<refi-prequal-no-offer
          class="side-padding"
        ></refi-prequal-no-offer>`;
      case REFI_PREQUAL_CONST.STATES.VERIFYING:
        if (this.isOtpExperiment) {
          return html`<request-one-time-passcode
            class="side-padding"
          ></request-one-time-passcode>`;
        }
        if (this.isIconicInvisiblePrequal) {
          return html`<iconic-verifying
            class="side-padding"
          ></iconic-verifying>`;
        }
        return html`<refi-prequal-verifying
          class="side-padding"
        ></refi-prequal-verifying>`;
      case REFI_PREQUAL_CONST.STATES.VERIFY_IDENTITY_ENTER_CODE:
        return html`<enter-one-time-passcode
          class="side-padding"
        ></enter-one-time-passcode>`;
      case REFI_PREQUAL_CONST.STATES.VERIFY_IDENTITY_FAILED:
        return html`<identity-verification-failed></identity-verification-failed>`;
      case REFI_PREQUAL_CONST.STATES.PROCESSING:
        return html`<refi-processing-softpull
          class="side-padding"
          ?is-iconic="${this.isIconicInvisiblePrequal || this.isIconicV2}"
          @initiate-animation-completion="${() => {
            this.initiateAnimationCompletion = true;
          }}"
          @show-completion-animation="${() => {
            this.showCompletionAnimation = true;
          }}"
        ></refi-processing-softpull>`;
      case REFI_PREQUAL_CONST.STATES.PROCESSING_PRE_APPROVAL:
        return html`<refi-prequal-processing-guaranteed-offer
          class="side-padding"
        ></refi-prequal-processing-guaranteed-offer>`;
      default:
        return html``;
    }
  }

  renderPageV2(currentState) {
    // Render based on current state
    switch (currentState) {
      case REFI_PAGES.CXT_COMBINED_HARDPULL:
        return html`<refi-cxt-combined-hardpull></refi-cxt-combined-hardpull>`;
      case REFI_PAGES.CREDIT_CONSENT:
        return html`<hardpull-primer></hardpull-primer>`;
      case REFI_PAGES.DELAYED_HARDPULL_PRIMER:
        return html`<delayed-hardpull-primer></delayed-hardpull-primer>`;
      case REFI_PAGES.DELAYED_HARDPULL_CONSENT:
        return html`<delayed-hardpull-consent></delayed-hardpull-consent>`;
      case REFI_PAGES.COMBINED_HP_CONTRACT:
        return html`<cxt-combined-hardpull></cxt-combined-hardpull>`;
      case REFI_PAGES.HARDPULL_REVIEWING:
        return html`<refi-reviewing-hardpull
          class="side-padding"
        ></refi-reviewing-hardpull>`;
      case REFI_PAGES.HARDPULL_PROCESSING:
        return html`<refi-processing-hardpull
          class="side-padding"
        ></refi-processing-hardpull>`;
      case REFI_PAGES.HARDPULL_DECLINED:
        return html`<refi-hardpull-decline
          class="side-padding"
        ></refi-hardpull-decline>`;
      case REFI_PREQUAL_CONST.STATES.VERIFY_IDENTITY_ENTER_CODE:
        return html`<enter-one-time-passcode
          class="side-padding"
        ></enter-one-time-passcode>`;
      case REFI_PREQUAL_CONST.STATES.VERIFY_IDENTITY_FAILED:
        return html`<identity-verification-failed></identity-verification-failed>`;
      default:
        return html``;
    }
  }

  renderTransitionPage() {
    if (
      (this.isIconicInvisiblePrequal || this.isIconicV2) &&
      this.showAnimation
    ) {
      return html`<refi-common-transition-page
        ?initiate-animation-completion="${this.initiateAnimationCompletion}"
        ?show-completion-animation="${this.showCompletionAnimation}"
        @curtain-close-initiated="${this.navigateToNextMfe}"
        minimum-animation-length-ms="2000"
        success-toggle-length-ms="1000"
      >
        <div id="animation" slot="animation">${softpullAnimation}</div>
        <fs-heading class="max-content-width" slot="animation-text">
          ${this.animationText}
        </fs-heading>
        <fs-heading slot="animation-text-complete">
          ${this.animationCompleteText}
        </fs-heading>
      </refi-common-transition-page>`;
    }
    return html``;
  }

  render() {
    this.debug.log('v2 routing', AppService.isV2Routing);
    this.debug.log('loading state: ', this.currentState);
    if (this.loadingApp || this.loadingAppIconic) {
      return html`<refi-loading-spinner
        class="_gb-show"
      ></refi-loading-spinner>`;
    }
    return html`${this.renderTransitionPage()}
    ${AppService.isV2Routing
      ? this.renderPageV2(this.currentStep)
      : this.renderPage(this.currentState)}`;
  }

  async firstUpdated() {
    this.loadingApp = true;
    Router.setRootElementForRouter();
    if (
      this.creditpullType === REFI_CREDITPULL_CONST.CREDITPULL_TYPE.SOFTPULL ||
      (this.testingParam === 'softpull' && !environment.isProdEnv)
    ) {
      if (this.postSoftpullState && this.postSoftpullState !== 'undefined') {
        this.navigatePostSoftpull();
        this.currentState = AppService.currentState;
        this.loadingApp = false;
      } else {
        AppService.creditpullType =
          REFI_CREDITPULL_CONST.CREDITPULL_TYPE.SOFTPULL;
        PrequalAppService.application =
          this.testingParam !== 'softpull' || environment.isProdEnv
            ? this.application
            : PrequalAppService.mockApplication;
        PrequalAppService.marketingSource = this.marketingSource;
        AppService.updateSoftpullState(
          REFI_PREQUAL_CONST.STATUSES.REVIEW,
          REFI_PREQUAL_CONST.STATES.REVIEW
        );
      }
      this.currentState = AppService.currentState;
      this.loadingApp = false;
    }
  }

  refreshSoftpullState() {
    AppService.creditpullType = REFI_CREDITPULL_CONST.CREDITPULL_TYPE.SOFTPULL;
    PrequalAppService.application =
      this.testingParam !== 'softpull' || environment.isProdEnv
        ? this.application
        : PrequalAppService.mockApplication;
    PrequalAppService.marketingSource = this.marketingSource;
    AppService.updateSoftpullState(AppService.status, AppService.currentState);
  }

  setupSession() {
    this.testingParam = new URL(window.location.href)?.searchParams?.get(
      'testing'
    );
    this.experimentParam = new URL(window.location.href).searchParams.get(
      'experiment'
    );
    if (this.experimentParam && !environment.isProdEnv) {
      this.experiments = {
        Iconic: {
          allocation: this.experimentParam,
          isEligible: true,
        },
      };
    }
    this.session = SessionService.session || '';
    if (this.testingParam === 'lighthouse-consent' && !environment.isProdEnv) {
      this.session = SessionService.mockConsentSession;
    }
    if (
      this.testingParam === 'lighthouse-combined-hp-cont' &&
      !environment.isProdEnv
    ) {
      this.session = SessionService.mockHardpullCombinedContractSession;
    }
    if (
      this.testingParam === 'lighthouse-hp-decline' &&
      !environment.isProdEnv
    ) {
      this.session = SessionService.mockHardpullDeclineSession;
      AppService.updateState({
        appStatusId: 402,
        statFlag: 61,
        updateRoute: true,
      });
    }
    if (this.testingParam === 'lighthouse-review' && !environment.isProdEnv) {
      this.session = SessionService.mockReviewingSession;
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    this.updateStratumMarketingChannel();
    this.loadingApp = false;
    this.onProcessingPage = false;
    this.currentState = AppService.currentState;
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(
      REFI_CREDITPULL_CONST.MARKETING_ATTRIBUTE_COOKIE
    )?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar(
        'marketingChannel',
        externalIdFromCookie ?? SessionService.getExternalId()
      );
    }
  }

  navigatePostSoftpull() {
    switch (this.postSoftpullState) {
      case 'Reviewing':
        AppService.updateSoftpullState(
          REFI_PREQUAL_CONST.STATUSES.REVIEWING,
          REFI_PREQUAL_CONST.STATES.REVIEWING
        );
        break;
      case 'Verifying':
        AppService.updateSoftpullState(
          REFI_PREQUAL_CONST.STATUSES.VERIFYING,
          REFI_PREQUAL_CONST.STATES.VERIFYING
        );
        break;
      case 'Declined':
        AppService.updateSoftpullState(
          REFI_PREQUAL_CONST.STATUSES.NO_OFFER,
          REFI_PREQUAL_CONST.STATES.NO_OFFER
        );
        break;
      default:
        AppService.updateSoftpullState(
          REFI_PREQUAL_CONST.STATUSES.REVIEWING,
          REFI_PREQUAL_CONST.STATES.REVIEWING
        );
    }
  }

  navigateToNextMfe() {
    if (
      this.creditpullType === REFI_CREDITPULL_CONST.CREDITPULL_TYPE.HARDPULL
    ) {
      const validStatuses = [405, 406, 407, 408];
      if (validStatuses.includes(AppService.state.appStatusId)) {
        this.debug.log('Redirecting back to host app');
        this.dispatchEvent(
          new CustomEvent('host-app-event', {
            bubbles: true,
            composed: true,
            detail: {
              actionName: 'next-mfe',
              data: {},
            },
          })
        );
      }
    }
    if (
      this.creditpullType === REFI_CREDITPULL_CONST.CREDITPULL_TYPE.SOFTPULL
    ) {
      this.debug.log('navigating to next mfe', AppService.status);
      if (
        AppService.status === REFI_PREQUAL_CONST.STATUSES.OFFER &&
        this.showCompletionAnimation
      ) {
        this.debug.log('Credit: Sending update-mfe-order event to host app');
        this.dispatchEvent(
          new CustomEvent('host-app-event', {
            bubbles: true,
            composed: true,
            detail: {
              actionName: 'update-mfe-order',
              from: 'creditpull-mfe',
              data: {
                eAppId: SessionService.session?.eAppId,
              },
            },
          })
        );
      } else if (this.currentState === REFI_PREQUAL_CONST.STATES.REVIEW) {
        document.dispatchEvent(
          new CustomEvent('show-error-page', {
            detail: { useDefaultPreAppDropErrorBodyText: true },
          })
        );
      }
    }
  }

  updateState() {
    this.currentState = AppService.currentState;
    this.debug.log('updating current state app.component: ', this.currentState);
    this.requestUpdate();
  }
}

customElements.define('refi-creditpull-app', RefiCreditpullApp);
